// useMarxentButton.js
import { ref, computed } from 'vue';
import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { OSVersionSupported } from '~/utils/OSVersionSupportedUtil';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';

/**
 * Composable to manage the Marxent CTA experience.
 * @param {(upcNumber: number, marxentApiCredentials: string | undefined) => Promise<{
    marxentUrl: import('vue').Ref<string | null | undefined>;
    isArAssetsAvailable: import('vue').Ref<boolean | null | undefined>;
}>} initMarxent - The function to initialize the Marxent URL.
 * @param {string | undefined} [marxentApiCredentials]
 */
function useMarxentButton(initMarxent, marxentApiCredentials) {
  const { isMcom } = useBrand();
  const { isHomeCategory, isSeasonalCelebrationsCategory } = useTopLevelCategoryFlags();

  /**
   * @type {import('vue').Ref<{ marxentUrl: import('vue').Ref<string | null | undefined>,
   *    isArAssetsAvailable: import('vue').Ref<boolean | null | undefined>} | null>}
   */

  const marxentValues = ref(null);
  // Computed property to determine if the AR experience is eligible.
  const isArReady = computed(() => isMcom && OSVersionSupported() && (isHomeCategory || isSeasonalCelebrationsCategory));
  const isArEligible = computed(() => !!marxentValues.value?.isArAssetsAvailable);

  const { isDesktop } = useDeviceType();

  const marxentATagStyle = `
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    color: #fff;
    text-decoration: none;
    padding: 6px;`;

  const marxentButtonStyles = isDesktop.value ? 'padding: 6px;' : marxentATagStyle;

  /**
   * Fetches the Marxent URL and updates the marxentUrl ref.
   * @param {number} upcNumber
   */
  const checkForMarxentAssetsAndUrl = async (upcNumber) => {
    if (isArReady.value) {
      try {
        const { marxentUrl, isArAssetsAvailable } = await initMarxent(upcNumber, marxentApiCredentials);
        marxentValues.value = { marxentUrl, isArAssetsAvailable };
      } catch (e) {
        console.error(e);
      }
    }
  };

  return {
    marxentValues,
    isArEligible,
    isArReady,
    marxentButtonStyles,
    checkForMarxentAssetsAndUrl,
  };
}

export default useMarxentButton;
